import React from "react";
import { Loader2, Mail, Unlink, RefreshCw, Inbox } from "lucide-react";
import { useGmail } from "../../../hooks/useGmail";
import { getInitial } from "../../emails/senderGrouping";

const EmailSettings: React.FC = () => {
  const {
    accounts: gmailAccounts,
    reconnectNeeded: gmailReconnectNeeded,
    isConnected: gmailConnected,
    isLoadingConnection: isLoadingGmail,
    connectGmail,
    disconnectGmail,
    disconnectingEmail,
  } = useGmail();

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-warm-text-primary mb-1">
          Email
        </h3>
        <p className="text-sm text-warm-text-secondary">
          Connect Gmail accounts to see your inbox grouped by sender.
        </p>
      </div>

      {/* One rule for every connected account, so it sits above them all */}
      <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-warm-bg-elevated">
        <Inbox size={16} className="text-warm-blue flex-shrink-0" />
        <p className="text-sm text-warm-text-secondary">
          JuDI shows every email you haven't archived, for all your
          connected accounts.
        </p>
      </div>

      {/* Gmail card */}
      <div className="border border-warm-border rounded-xl overflow-hidden">
        <div className="flex items-center gap-3 p-4 bg-warm-bg-elevated">
          <div className="w-9 h-9 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
            <Mail size={18} className="text-red-600 dark:text-red-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-warm-text-primary text-sm">Gmail</p>
            <p className="text-xs text-warm-text-tertiary truncate">
              {gmailConnected
                ? `${gmailAccounts.length} account${gmailAccounts.length > 1 ? "s" : ""} connected`
                : "Not connected"}
            </p>
          </div>
          {isLoadingGmail && (
            <Loader2 size={16} className="animate-spin text-warm-text-tertiary" />
          )}
          {!isLoadingGmail && !gmailConnected && (
            <button
              onClick={connectGmail}
              className="flex items-center gap-1.5 text-xs text-white bg-warm-blue hover:bg-warm-blue-dark transition-colors px-3 py-1.5 rounded-lg"
            >
              <RefreshCw size={13} />
              Connect
            </button>
          )}
        </div>

        {gmailConnected && (
          <div className="border-t border-warm-border">
            {gmailAccounts.map((account) => (
              <div key={account.gmail_email} className="px-4 py-3 border-b border-warm-border">
                <div className="flex items-center gap-3">
                  {account.avatar_url ? (
                    <img
                      src={account.avatar_url}
                      alt=""
                      referrerPolicy="no-referrer"
                      className="w-7 h-7 rounded-full flex-shrink-0 object-cover"
                    />
                  ) : (
                    <span
                      aria-hidden="true"
                      className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 bg-warm-bg-elevated text-warm-blue"
                    >
                      {getInitial(account.gmail_email)}
                    </span>
                  )}
                  <p className="flex-1 min-w-0 text-sm font-medium text-warm-text-primary truncate">
                    {account.gmail_email}
                  </p>
                  <button
                    onClick={() => disconnectGmail(account.gmail_email)}
                    disabled={disconnectingEmail === account.gmail_email}
                    aria-label={
                      disconnectingEmail === account.gmail_email
                        ? `Disconnecting ${account.gmail_email}`
                        : `Disconnect ${account.gmail_email}`
                    }
                    className="flex items-center gap-1.5 text-xs text-warm-text-secondary hover:text-red-600 dark:hover:text-red-400 transition-colors px-2 py-1 rounded-lg border border-warm-border hover:border-red-300 dark:hover:border-red-700 flex-shrink-0 disabled:opacity-50 disabled:pointer-events-none"
                  >
                    {disconnectingEmail === account.gmail_email ? (
                      <>
                        <Loader2 size={12} className="animate-spin" />
                        Disconnecting…
                      </>
                    ) : (
                      <>
                        <Unlink size={12} />
                        Disconnect
                      </>
                    )}
                  </button>
                </div>
                {gmailReconnectNeeded.includes(account.gmail_email) && (
                  <div className="mt-3 flex items-center justify-between gap-2 rounded-lg bg-warm-bg-elevated px-3 py-2">
                    <p className="text-xs text-warm-text-secondary">
                      Connection expired — reconnect to keep syncing this inbox.
                    </p>
                    <button
                      onClick={connectGmail}
                      className="flex items-center gap-1.5 text-xs text-white bg-warm-blue hover:bg-warm-blue-dark transition-colors px-3 py-1.5 rounded-lg flex-shrink-0"
                    >
                      <RefreshCw size={13} />
                      Reconnect
                    </button>
                  </div>
                )}
              </div>
            ))}

            <div className="p-4 space-y-2">
              <button
                onClick={connectGmail}
                className="flex items-center gap-1.5 text-xs font-medium text-warm-blue hover:text-warm-blue-dark transition-colors"
              >
                <RefreshCw size={13} />
                Connect another account
              </button>
              <p className="text-xs text-warm-text-tertiary leading-relaxed">
                Connected with a different Google account than the one you
                log in with? That's fine — you can manage multiple inboxes
                here.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailSettings;
